/**
 * Color tokens for the Serenity client.
 *
 * Aligned with Material 3 color roles:
 * https://m3.material.io/styles/color/roles
 *
 * ───────────────────────────────────────────────────────────────────────
 * Material 3 layering model (how to think about these tokens)
 * ───────────────────────────────────────────────────────────────────────
 *
 * 1. ROLE FAMILIES — each color role comes as a paired set:
 *
 *      primary             — saturated brand color (foregrounds, filled
 *                            buttons, focused indicators)
 *      onPrimary           — text/icon drawn on a `primary` fill
 *      primaryContainer    — low-emphasis tonal surface in the brand
 *                            family (tonal buttons, chips, selected items)
 *      onPrimaryContainer  — text/icon drawn on `primaryContainer`
 *
 *    Same pattern for `error` (destructive) and `tertiaryContainer`
 *    (contrast-accent highlights — Serenity uses only the container tint).
 *
 *    Pairing rule: every surface token has a matching `on*` foreground.
 *    Components pair them: `surfaceContainer` bg → `onSurface` text;
 *    `primaryContainer` bg → `onPrimaryContainer` text; `error` bg →
 *    `onError` text. This is what makes theming drop-in.
 *
 * 2. SURFACE LADDER — backgrounds are a stack, not one color:
 *
 *      surface                  — app baseline background
 *      surfaceContainer         — nested/contained content (chat bubbles,
 *                                 text inputs, cards)
 *      surfaceContainerHighest  — most-elevated clean surface (floating
 *                                 white pills)
 *      surfaceVariant           — muted/de-emphasized surface
 *                                 (inactive controls)
 *      inverseSurface           — reversed-contrast dark surface for
 *                                 attention (toasts, error notifications)
 *
 *    Every surface pairs with an `onSurface*` text token — or with
 *    `inverseOnSurface` for the inverse surface.
 *
 * 3. NON-SURFACE TOKENS — elements that aren't backgrounds:
 *
 *      outline / outlineVariant  — borders (default / subtle warm)
 *      scrim                     — translucent modal dimmer. NOT a
 *                                  surface — content shows through,
 *                                  unlike `inverseSurface`.
 *      state*Layer               — interaction overlays (hover, pressed)
 *      shadow                    — shadow cast color
 *
 * ───────────────────────────────────────────────────────────────────────
 * How to pick a token
 * ───────────────────────────────────────────────────────────────────────
 *
 * Ask what ROLE the element plays, not what COLOR you want:
 *   - Ordinary card content          → `surfaceContainer` / `onSurface`
 *   - Brand-aligned interactive fill → `primaryContainer` / `onPrimaryContainer`
 *     (Serenity's "soft tonal" button style — see note below)
 *   - Destructive action             → `error` / `onError`
 *   - Border on a neutral surface    → `outline`
 *   - Focused input or active icon   → `primary`
 *   - AI-generated content surface   → `aiContainer` (app extension)
 *
 * ───────────────────────────────────────────────────────────────────────
 * Serenity's button-style deviation
 * ───────────────────────────────────────────────────────────────────────
 *
 * M3's canonical "filled tonal button" uses the `secondaryContainer`
 * family. Serenity uses `primaryContainer` + `onPrimaryContainer` — the
 * same tonal shape sourced from the primary family, because the app has
 * one brand color. The pairing itself is M3-compliant; the source-family
 * choice is a documented variant chosen for the therapy-app aesthetic
 * (soft, low-arousal). Reserve M3's canonical high-emphasis filled
 * pattern (`primary` bg + `onPrimary` white text) for genuine urgency —
 * currently only destructive actions, which use the `error` family.
 *
 * ───────────────────────────────────────────────────────────────────────
 * Architecture
 * ───────────────────────────────────────────────────────────────────────
 *
 *   - `palette` (module-private) — role-named primitive values. Names
 *     describe position in the tonal scale, not hue, so a future
 *     dark-mode mapping can swap values without renaming keys.
 *   - `colors` (exported) — M3-aligned semantic tokens. Every value is
 *     `palette.X` or `alpha(palette.X, n)`. No raw color strings here.
 *
 * Tokens marked "(app extension)" have no canonical M3 role — the app
 * needs a role M3 doesn't define (AI-content marker, success feedback,
 * third text tier).
 */

// -----------------------------------------------------------------------
// Tier 1 — palette (primitives, module-private)
// -----------------------------------------------------------------------

const palette = {
  // Brand
  brand: "rgb(70, 103, 97)", // sage green
  brandContainer: "rgb(205, 218, 207)", // pale sage

  // Warm neutral surface ladder (cream family)
  surfaceBase: "rgb(244, 244, 239)", // cream
  surfaceWarm: "rgb(255, 249, 238)", // warmer cream
  surfaceEdge: "rgb(239, 235, 230)", // warm off-white

  // Cool neutrals (borders + muted surfaces)
  neutralLine: "rgb(211, 208, 208)", // cool gray line
  neutralSurface: "rgb(240, 240, 240)", // cool near-white surface

  // Ink (text emphasis ladder on light surfaces)
  inkHigh: "rgb(74, 74, 74)",
  inkMid: "rgb(102, 102, 102)",
  inkLow: "rgb(156, 163, 175)",

  // Accent + semantic hues
  tertiary: "rgb(214, 236, 245)", // soft blue — contrast highlight
  ai: "rgb(226, 224, 235)", // purple — AI content marker
  aiDark: "rgb(118, 108, 165)", // deep purple — on-surface text/icons over aiContainer
  danger: "rgb(220, 53, 69)", // destructive red
  dangerContainer: "rgb(250, 208, 208)", // pale red — tonal destructive surface

  // Theme anchors (black/white used for scrims, shadows, on-saturated text)
  black: "rgb(0, 0, 0)",
  white: "rgb(255, 255, 255)"
} as const;

/** Apply opacity to an rgb primitive. */
const alpha = (rgb: string, a: number) =>
  rgb.replace("rgb(", "rgba(").replace(")", `, ${a})`);

// -----------------------------------------------------------------------
// Tier 2 — colors (M3 semantic tokens, exported)
// -----------------------------------------------------------------------

export const colors = {
  // ─── Primary (brand) ─────────────────────────────────────────────────
  // The app's main brand color. In M3, `primary` IS the brand — there is
  // no separate "brand" concept. Use at two intensities: `primary` (full
  // saturation, for emphasis) and `primaryContainer` (tonal, for
  // brand-aligned surfaces).

  /**
   * Main brand color. Use as a foreground accent (icons, links, focused
   * input borders) and as a solid fill for the most prominent interactive
   * elements. Pair with `onPrimary` when used as a fill.
   * M3: `primary`.
   */
  primary: palette.brand,

  /**
   * Text or icons drawn on a `primary` fill. Pure white.
   * M3: `on-primary`.
   */
  onPrimary: palette.white,

  /**
   * Low-emphasis brand surface — a tonal variant of `primary`. Use for
   * active primary buttons, selected dropdown items, and any container
   * that should read as brand-aligned without shouting.
   * Pair with `onPrimaryContainer`. M3: `primary-container`.
   */
  primaryContainer: palette.brandContainer,

  /**
   * Text or icons drawn on a `primaryContainer` fill. The canonical M3
   * pairing for the app's tonal-button style: saturated sage label on a
   * pale sage surface. Same RGB as `primary` today, but kept as a
   * distinct token so a future theme can split the two without editing
   * components.
   * M3: `on-primary-container`.
   */
  onPrimaryContainer: palette.brand,

  // ─── Error ───────────────────────────────────────────────────────────
  // Destructive / error feedback. Separate family from `primary` so
  // destructive actions never borrow the brand color.

  /**
   * Destructive-action color. Use as foreground for warnings and as a
   * solid fill for destructive-action buttons (Delete, Remove). Pair with
   * `onError` when used as a fill.
   * M3: `error`.
   */
  error: palette.danger,

  /**
   * Text or icons drawn on an `error` fill. Pure white.
   * M3: `on-error`.
   */
  onError: palette.white,

  /**
   * Low-emphasis destructive surface — a tonal variant of `error`, mirroring
   * `primaryContainer`. Use for destructive-action buttons that should read as
   * dangerous without the full-saturation solid fill: saturated red label on a
   * pale red surface. Pair with `onErrorContainer`. M3: `error-container`.
   */
  errorContainer: palette.dangerContainer,

  /**
   * Text or icons drawn on an `errorContainer` fill. The destructive analog of
   * `onPrimaryContainer` — same red as `error`, kept distinct so a future theme
   * can split the two without editing components.
   * M3: `on-error-container`.
   */
  onErrorContainer: palette.danger,

  // ─── Tertiary (contrast accent) ──────────────────────────────────────
  // A contrasting-accent family used for highlights that balance `primary`
  // without carrying brand weight. The app only uses the container tint,
  // so `tertiary` itself is not exported (yet).

  /**
   * Contrasting-accent surface that balances `primary`. Use for
   * attention-drawing highlights that aren't brand-owned: currently-
   * playing rows, incoming chat bubbles.
   * Pair with `onSurface`. M3: `tertiary-container`.
   */
  tertiaryContainer: palette.tertiary,

  // ─── Surface family ──────────────────────────────────────────────────
  // Surfaces are the backgrounds content sits on. M3 models them as a
  // ladder: `surface` is the baseline plane; `surfaceContainer` and
  // `surfaceContainerHighest` are elevated variants; `surfaceVariant` is
  // a muted alternative. Every surface pairs with an `onSurface*` text
  // token for contrast.

  /**
   * The app's default background plane — the surface everything else
   * sits on. Pair with `onSurface`.
   * M3: `surface`.
   */
  surface: palette.surfaceBase,

  /**
   * Nested/contained content surface — one tonal step up from `surface`.
   * Use for chat bubbles, text-input fills, info cards: anything that
   * should read as "inside" the page.
   * Pair with `onSurface`. M3: `surface-container`.
   */
  surfaceContainer: palette.surfaceWarm,

  /**
   * Most-elevated clean surface. Pure white, for cards/pills that should
   * visually float above the warm stack.
   * Pair with `onSurface`. M3: `surface-container-highest`.
   */
  surfaceContainerHighest: palette.white,

  /**
   * Muted/de-emphasized surface. Use for inactive or low-priority
   * controls (inactive secondary buttons).
   * Pair with `onSurface`. M3: `surface-variant`.
   */
  surfaceVariant: palette.neutralSurface,

  // ─── Text (on-surface hierarchy) ─────────────────────────────────────
  // Foreground text/icons drawn on any surface/container. Three emphasis
  // levels (M3 standardizes two; the third is an app extension).

  /**
   * High-emphasis text on any surface/container. Body copy, headings.
   * M3: `on-surface`.
   */
  onSurface: palette.inkHigh,

  /**
   * Medium-emphasis text on surfaces. Supporting copy, metadata.
   * M3: `on-surface-variant`.
   */
  onSurfaceVariant: palette.inkMid,

  /**
   * Low-emphasis text on surfaces. Placeholders, captions.
   * App extension — M3 defines 2 text tiers; the app uses 3.
   */
  onSurfaceMuted: palette.inkLow,

  // ─── Outline (borders) ───────────────────────────────────────────────
  // Border/divider colors. `outline` is the default cool-gray line;
  // `outlineVariant` is a warmer, subtler edge for form fields on warm
  // surfaces.

  /**
   * Default border on neutral surfaces — card outlines, dividers.
   * M3: `outline`.
   */
  outline: palette.neutralLine,

  /**
   * Subtle warm border. Used on form fields that sit on warm surfaces;
   * blends into `surfaceContainer` better than the cool `outline`.
   * Focused inputs switch to `primary`.
   * M3: `outline-variant`.
   */
  outlineVariant: palette.surfaceEdge,

  // ─── Inverse (dark attention surfaces) ───────────────────────────────
  // "Inverse" in M3 = reversed-contrast surfaces, used sparingly for
  // elements that must grab attention against the light theme: toasts,
  // error notifications. Distinct from `scrim`, which dims rather than
  // replaces.

  /**
   * Dark reversed surface — a near-opaque dark plane that pops against
   * the light app. Use for toasts and error notifications.
   * Pair with `inverseOnSurface`. M3: `inverse-surface`.
   */
  inverseSurface: alpha(palette.black, 0.8),

  /**
   * Text drawn on `inverseSurface`. White at 90% — softened from pure
   * white for legibility comfort.
   * M3: `inverse-on-surface`.
   */
  inverseOnSurface: alpha(palette.white, 0.9),

  // ─── App extensions (no M3 role) ─────────────────────────────────────
  // Roles M3 doesn't define but the app needs. Named in the M3 style
  // (`*Container` for surfaces) and documented as extensions so they're
  // not mistaken for canonical M3 tokens.

  /**
   * AI-generated content marker. Applied as a subtle purple tint to any
   * surface whose content was produced by the AI agent — pattern cards,
   * AI accordion headers, insight summaries.
   * Pair with `onSurface` for body copy, `onAiContainer` for headers.
   * App extension — not an M3 role.
   */
  aiContainer: palette.ai,

  /**
   * Slightly deeper variant of `aiContainer` — for elements nested on an
   * `aiContainer` surface that need to stand out (e.g., an evidence
   * accordion sitting inside a pattern card). Pair with `onSurface` /
   * `onAiContainer`. App extension — not an M3 role.
   */
  aiContainerHigh: alpha(palette.aiDark, 0.08),

  /**
   * Accent text/icon color drawn on an `aiContainer` fill. Deep purple,
   * used for card header titles and leading icons to distinguish them from
   * body copy on the same surface. Follows M3's `on*Container` pairing
   * pattern. App extension — not an M3 role.
   */
  onAiContainer: palette.aiDark,

  /**
   * Success-feedback surface. Light green tint for "success" / "info"
   * notifications.
   * Pair with `onSurface`. App extension — M3 has no success role.
   */
  successContainer: alpha(palette.brandContainer, 0.2),

  // ─── Scrim (modal backdrop) ──────────────────────────────────────────
  // A single token: the translucent curtain behind modals. Not a surface
  // (content shows through). Standard term across Material, Android, and
  // iOS HIG.

  /**
   * Translucent dim layer drawn behind modal dialogs and sheets. NOT a
   * surface — you can see through it. Black at 50%.
   * M3: `scrim`.
   */
  scrim: alpha(palette.black, 0.5),

  // ─── State layers ────────────────────────────────────────────────────
  // Translucent overlays drawn on a base surface to signal interaction.
  // Orthogonal to surface styling. (M3 state-layer pattern.)

  /** Hover overlay — black at 4%. M3 state layer (hover). */
  stateHoverLayer: alpha(palette.black, 0.04),

  /** Pressed overlay — black at 8%. M3 state layer (pressed). */
  statePressedLayer: alpha(palette.black, 0.08),

  /** Opacity multiplier for disabled elements. App convention. */
  disabledOpacity: 0.5,

  // ─── Shadow ──────────────────────────────────────────────────────────

  /** Shadow cast color (consumed by `constants/shadows.ts`). M3: `shadow`. */
  shadow: palette.black
} as const;
